import type { StandardOutlet } from "building-types/electrical/electricalLayoutTypes";
import { LoadTypeSlot } from "building-types/electrical/electricalSlots";
import type { Load } from "building-types/electrical/generatedElectricalTypes";
import { WallType } from "building-types/walls/wallEnums";
import { getLoadCatalog } from "doc/getLoadCatalog";
import { f2i } from "doc/measurementUtils";
import { polyLine2EdgeEval, polyLine2EdgeNormal, polyLine2Length } from "geom/polyLine2";
import { rangeNonInclusiveByCount } from "geom/range";
import { cutGapsIntoIntervalStartingAtZero } from "../../../utils/intervals";
import type { ElectricalLayoutFunc, ElectricalLayoutOpts } from "../electricalLayoutTypes";
import { loadTypeFromSlots } from "../loadTypeFromSlots";

export function standardOutlets({
  loadSlot = LoadTypeSlot.OUTLET,
  loadType,
  outletSpacing: wallOutletSpacing = f2i(6),
  minWallLength = f2i(2),
  singleOutletMax = f2i(12),
}: StandardOutlet): ElectricalLayoutFunc {
  return async (opts: ElectricalLayoutOpts) => {
    const { room, wallIndex, elecSlots, electricalItemCatalogSettings } = opts;
    const resolvedLoadType = loadType ?? loadTypeFromSlots(loadSlot, elecSlots);
    const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
    const loadDescriptor = loadCatalog[resolvedLoadType];
    const loads: Load[] = [];
    const roomStrips = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType !== WallType.VOID, true);

    for (const strip of roomStrips) {
      const stripEdge = strip.baseline(true, false, true);
      const [nX, nY] = polyLine2EdgeNormal(stripEdge, 0, true);
      const stripLengthDim = polyLine2Length(stripEdge);
      const doorGaps = strip.doorGaps(false);
      doorGaps.sort((a, b) => a.start - b.start);

      const gapIntervals: number[] = [];
      for (const { start, end } of doorGaps) {
        gapIntervals.push(start);
        gapIntervals.push(end);
      }

      const outletIntervals = cutGapsIntoIntervalStartingAtZero(stripLengthDim, gapIntervals);
      for (let i = 0, l = outletIntervals.length; i < l; i += 2) {
        const start = outletIntervals[i];
        const end = outletIntervals[i + 1];
        const distance = end - start;
        let outletCount = 1;
        if (distance < minWallLength) {
          continue;
        } else if (distance < singleOutletMax) {
          outletCount = 1;
        } else {
          outletCount = Math.floor(distance / wallOutletSpacing);
        }

        const outletParams = rangeNonInclusiveByCount(start, end, outletCount);

        for (const outletParam of outletParams) {
          const [x, y] = polyLine2EdgeEval(stripEdge, 0, outletParam, false);

          loads.push({
            loadType: resolvedLoadType,
            loadDescriptor,
            position: [x, y],
            normal: [nX, nY],
            levelId: room.level.id,
            sourceRoomHandle: room.roomHandle,
            hideConnections: true,
          });
        }
      }
    }

    return [
      {
        levelId: opts.room.level.id,
        loads,
        controllers: [],
      },
    ];
  };
}
